// R58-L-04 — central filter hook for the 4-level filter tree.
//
// The hook owns the URL ↔ Zustand bridge for the analysis-page filter
// state. Five widgets subscribe to it (filmstrip, image list, AI-findings
// list, defects list, Tiles widget) and pass the resulting axis arrays
// down to their TanStack queries.
//
// URL shape (CSV per axis; empty array = param omitted):
//   ?reviewState=seen,unseen
//   &findingKind=ai,defect
//   &side=LE,TE,PS,SS
//   &severity=1,2,3,4,5
//   &defectClassIds=<uuid>,<uuid>     (R58-L-05 — defect text-search results)
//
// Hydration on mount: each URL axis maps into the underlying
// `useAnalysisFilterStore` so widgets that read the Zustand store keep
// working without a rewrite. The hook returns the canonical accessor +
// setter surface; legacy consumers (FilmstripWidget, TilesWidget,
// SketchWidget, FindingsPanel, DefectsPanel) can keep reading
// `useAnalysisFilterStore(s => s.sides)` directly — that's the same
// state.
//
// Decisions locked in §10 of `.vibe/08_STORY_BOOK_5.md`:
//   * Q4 — L2 multi-select with `none = both kinds`.
//   * Q5 — L2 Defects re-enables eagerly when first defect lands (the
//     hook surfaces the `defectsCount` consumers need; the re-enable is
//     a render-cycle invalidation, not a hook-level effect).
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useAnalysisFilterStore,
  type ReviewVisibilityKey,
  type SideKey,
} from "./state/analysisFilterStore.js";

export type FindingKind = "ai" | "defect";

const SIDE_KEYS: readonly SideKey[] = ["LE", "TE", "PS", "SS"];
const REVIEW_KEYS: readonly ReviewVisibilityKey[] = ["seen", "unseen"];
const FINDING_KIND_KEYS: readonly FindingKind[] = ["ai", "defect"];

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function parseReviewState(raw: string | null): ReviewVisibilityKey[] {
  return parseCsv(raw).filter((v): v is ReviewVisibilityKey =>
    REVIEW_KEYS.includes(v as ReviewVisibilityKey),
  );
}

function parseFindingKind(raw: string | null): FindingKind[] {
  return parseCsv(raw).filter((v): v is FindingKind =>
    FINDING_KIND_KEYS.includes(v as FindingKind),
  );
}

function parseSide(raw: string | null): SideKey[] {
  return parseCsv(raw).filter((v): v is SideKey =>
    SIDE_KEYS.includes(v as SideKey),
  );
}

function parseSeverity(raw: string | null): number[] {
  return parseCsv(raw)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 99);
}

function toCsvOrUndef(
  values: readonly (string | number)[],
): string | undefined {
  return values.length === 0 ? undefined : values.join(",");
}

function arraysEqual<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export interface UseAnalysisFilterResult {
  /** L1 — multi-select. Empty = bypass. */
  reviewState: readonly ReviewVisibilityKey[];
  /** L2 — multi-select. Empty = bypass (both kinds). */
  findingKind: readonly FindingKind[];
  /** L3 — multi-select. Empty = bypass (all sides). */
  side: readonly SideKey[];
  /** L4 — multi-select. Empty = bypass (all grades). */
  severity: readonly number[];
  /**
   * R58-L-05 — defect class entry ids picked via the
   * `<DefectClassSearch />` typeahead. AND-composed with the rest of the
   * tree. Empty = bypass.
   */
  defectClassIds: readonly string[];

  setReviewState: (next: readonly ReviewVisibilityKey[]) => void;
  setFindingKind: (next: readonly FindingKind[]) => void;
  setSide: (next: readonly SideKey[]) => void;
  setSeverity: (next: readonly number[]) => void;
  setDefectClassIds: (next: readonly string[]) => void;
  resetAll: () => void;
}

/**
 * Canonical hook for the analysis-page filter tree. Reads + writes URL
 * search params and reflects them into the underlying Zustand store so
 * legacy consumers (FilmstripWidget, TilesWidget, SketchWidget,
 * DefectsPanel, FindingsPanel) keep working unchanged.
 *
 * The Zustand store remains the per-axis storage for `severities`,
 * `sides`, `statuses`, `q`, etc. — the hook just owns the URL
 * round-trip. This keeps the `applyFilters` + `computeCounts`
 * pure-function pipeline that downstream code depends on.
 */
export function useAnalysisFilter(): UseAnalysisFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const sides = useAnalysisFilterStore((s) => s.sides);
  const severities = useAnalysisFilterStore((s) => s.severities);
  const visibility = useAnalysisFilterStore((s) => s.visibility);
  const setSides = useAnalysisFilterStore((s) => s.setSides);
  const setSeverities = useAnalysisFilterStore((s) => s.setSeverities);
  const setVisibility = useAnalysisFilterStore((s) => s.setVisibility);
  const hydrate = useAnalysisFilterStore((s) => s.hydrate);
  const blades = useAnalysisFilterStore((s) => s.blades);
  const statuses = useAnalysisFilterStore((s) => s.statuses);
  const q = useAnalysisFilterStore((s) => s.q);

  // The findingKind + defectClassIds axes are new — the Zustand store
  // doesn't carry them yet. They live in component-internal refs +
  // URL-only state; the URL is the source of truth. Five-consumer
  // subscribers read them directly off the hook, not the store.
  //
  // Note: future refactor could promote them into the store. For now
  // we keep them URL-only to minimize the blast radius of this rewrite.
  const findingKind = useMemo<FindingKind[]>(
    () => parseFindingKind(searchParams.get("findingKind")),
    [searchParams],
  );
  const defectClassIds = useMemo<string[]>(
    () => parseCsv(searchParams.get("defectClassIds")),
    [searchParams],
  );

  // ---- URL → store hydration (mount + URL-change) -------------------
  //
  // Track the last params we observed so we don't fight ourselves: when
  // the user toggles a chip, we write to URL → URL change fires this
  // effect → we'd re-write the store with the same value. That's a
  // no-op in steady state, but during fast multi-axis updates the
  // round-trip can race. The `lastSeenRef` guard keeps us from
  // re-hydrating from a URL we just wrote.
  const lastSeenRef = useRef<string>("");
  useEffect(() => {
    const fingerprint = `${searchParams.get("reviewState") ?? ""}|${searchParams.get("side") ?? ""}|${searchParams.get("severity") ?? ""}`;
    if (fingerprint === lastSeenRef.current) return;
    lastSeenRef.current = fingerprint;
    const nextReview = parseReviewState(searchParams.get("reviewState"));
    const nextSide = parseSide(searchParams.get("side"));
    const nextSeverity = parseSeverity(searchParams.get("severity"));
    const sameReview = arraysEqual(nextReview, visibility);
    const sameSide = arraysEqual(nextSide, sides);
    const sameSev = arraysEqual(nextSeverity, severities);
    if (sameReview && sameSide && sameSev) return;
    hydrate({
      blades,
      sides: nextSide,
      severities: nextSeverity,
      statuses,
      visibility: nextReview,
      q,
    });
  }, [searchParams]);

  // ---- Setters that write through to URL + store --------------------

  const writeUrl = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const setReviewState = useCallback(
    (nextValue: readonly ReviewVisibilityKey[]) => {
      setVisibility(nextValue);
      writeUrl((next) => {
        const csv = toCsvOrUndef(nextValue);
        if (csv == null) next.delete("reviewState");
        else next.set("reviewState", csv);
      });
    },
    [setVisibility, writeUrl],
  );

  const setFindingKind = useCallback(
    (nextValue: readonly FindingKind[]) => {
      writeUrl((next) => {
        const csv = toCsvOrUndef(nextValue);
        if (csv == null) next.delete("findingKind");
        else next.set("findingKind", csv);
      });
    },
    [writeUrl],
  );

  const setSide = useCallback(
    (nextValue: readonly SideKey[]) => {
      setSides(nextValue);
      writeUrl((next) => {
        const csv = toCsvOrUndef(nextValue);
        if (csv == null) next.delete("side");
        else next.set("side", csv);
      });
    },
    [setSides, writeUrl],
  );

  const setSeverity = useCallback(
    (nextValue: readonly number[]) => {
      setSeverities(nextValue);
      writeUrl((next) => {
        const csv = toCsvOrUndef(nextValue);
        if (csv == null) next.delete("severity");
        else next.set("severity", csv);
      });
    },
    [setSeverities, writeUrl],
  );

  const setDefectClassIds = useCallback(
    (nextValue: readonly string[]) => {
      writeUrl((next) => {
        const csv = toCsvOrUndef(nextValue);
        if (csv == null) next.delete("defectClassIds");
        else next.set("defectClassIds", csv);
      });
    },
    [writeUrl],
  );

  const resetAll = useCallback(() => {
    setVisibility([]);
    setSides([]);
    setSeverities([]);
    writeUrl((next) => {
      next.delete("reviewState");
      next.delete("findingKind");
      next.delete("side");
      next.delete("severity");
      next.delete("defectClassIds");
    });
  }, [setVisibility, setSides, setSeverities, writeUrl]);

  return {
    reviewState: visibility,
    findingKind,
    side: sides,
    severity: severities,
    defectClassIds,
    setReviewState,
    setFindingKind,
    setSide,
    setSeverity,
    setDefectClassIds,
    resetAll,
  };
}
